import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import {
  GuiaBreadcrumb,
  ConversionBlock,
  PriceTag,
  GuiaFAQ,
  GuiaRelacionadas,
  SchemaArticle,
  SchemaFAQPage,
} from '../components/GuiaComponents';
import ReassuranceSection from '@/components/feature/ReassuranceSection';

const FAQS = [
  {
    question: '¿Cuánto cuesta cambiar la lona de un toldo en Málaga?',
    answer:
      'El precio depende del tamaño y del tipo de tejido. Para un toldo estándar de terraza (unos 3-4 metros de ancho), el cambio de lona con tejido acrílico de calidad ronda los 120-200 €, mano de obra incluida. Lonas más grandes o con tejidos técnicos (microperforados, impermeabilizados) pueden alcanzar los 350-500 €.',
  },
  {
    question: '¿Cada cuánto hay que cambiar la lona de un toldo?',
    answer:
      'En condiciones normales, una lona de toldo de buena calidad dura entre 8 y 12 años. Pero en Málaga, con más de 300 días de sol intenso al año y la exposición al salitre costero, la vida útil se reduce a 5-8 años. Los primeros signos de que toca cambiar son: decoloración marcada, pequeñas rasgaduras en los bordes, pérdida de tensión al extender, y manchas de humedad que ya no salen con la limpieza.',
  },
  {
    question: '¿Puedo cambiar yo mismo la lona de mi toldo?',
    answer:
      'Técnicamente es posible si tienes dos personas, una escalera estable y mucha paciencia. Pero ten en cuenta: hay que desmontar la lona vieja del tubo de enrollado y del perfil de carga delantero, medir y cortar la nueva con precisión milimétrica, y volver a tensarla correctamente. Una lona mal tensada se descuelga en semanas y pierde toda su estética. Además, trabajar a cierta altura con el toldo extendido conlleva riesgos.',
  },
  {
    question: '¿Qué tipo de tela es mejor para toldos en Málaga?',
    answer:
      'Para el clima de Málaga recomendamos tejido acrílico 100% solución tintada (como Sunbrella o Recacril). Este material resiste mejor los rayos UV, no se decolora con el sol intenso, repele el agua de las lluvias torrenciales ocasionales y transpira para evitar la acumulación de moho. El poliéster es más barato pero dura la mitad y pierde el color en 2-3 temporadas de sol malagueño.',
  },
];

export default function GuiaCambiarLonaToldo() {
  return (
    <>
      <title>Cambiar la lona o tela de un toldo | Toldos Málaga</title>
      <meta
        name="description"
        content="¿Necesitas cambiar la lona o tela de tu toldo? Guía completa: cuándo cambiarla, tipos de tejido, precios y reparación profesional en Málaga. Presupuesto gratis."
      />
      <meta
        name="keywords"
        content="cambiar lona toldo, cambiar tela toldo, cambiar la lona del toldo, cuanto cuesta cambiar la tela de un toldo, lona toldo malaga"
      />
      <link rel="canonical" href="https://toldos-malaga.es/guias/cambiar-lona-toldo" />
      <SchemaArticle
        headline="Cambiar la lona o tela de un toldo: guía completa"
        description="Todo lo que necesitas saber sobre el cambio de lona de toldo: señales de desgaste, tipos de tejido recomendados para el clima de Málaga, precios orientativos y proceso de sustitución profesional."
        datePublished="2025-11-22"
        dateModified="2026-06-16"
        imageUrl="/images/007-guia-cambiar-lona-og.webp"
        canonicalUrl="https://toldos-malaga.es/guias/cambiar-lona-toldo"
      />
      <SchemaFAQPage faqs={FAQS} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Cambio de lona de toldo en Málaga',
              description: 'Sustitución profesional de la lona o tela de toldos en Málaga. Tejidos acrílicos de alta resistencia UV. Medición, confección e instalación incluida.',
            },
            priceSpecification: {
              '@type': 'PriceSpecification',
              price: 120,
              priceCurrency: 'EUR',
            },
          }),
        }}
      />
      <Navbar />
      <main>
        <GuiaBreadcrumb currentTitle="Cambiar la lona o tela de un toldo" />

        <section className="w-full max-w-[1280px] mx-auto px-4 md:px-6 pt-6 pb-10">
          <div className="relative rounded-2xl overflow-hidden h-[280px] md:h-[380px]">
            <img
              src="/images/006-guia-cambiar-lona-hero.webp"
              alt="Lona de toldo desgastada y decolorada por el sol en Málaga"
              title="Cambio de lona de toldo en Málaga - Reparación profesional"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/15 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-500/90 text-foreground-900 text-xs font-semibold mb-3">
                Guía de reparación
              </span>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white">
                Cambiar la lona o tela de un toldo
              </h1>
            </div>
          </div>
        </section>

        <ReassuranceSection />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-14">
          <div className="prose-custom space-y-6 text-foreground-700 leading-relaxed">

            <p className="text-lg text-foreground-600">
              La lona es la cara visible de tu toldo, pero también su elemento más expuesto. En Málaga, el sol implacable, las lluvias torrenciales de otoño y el salitre marino forman una combinación que castiga los tejidos sin piedad. Cambiar la lona a tiempo no solo mejora la estética de tu terraza: evita que una lona deteriorada dañe el mecanismo del toldo al forzarlo con desgarros y descolgamientos.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Señales claras de que toca cambiar la lona
            </h2>

            <p>
              No hace falta ser un experto para detectar que una lona ha llegado al final de su vida útil. Estas son las señales más evidentes:
            </p>

            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-contrast-drop-line text-primary-600 text-sm"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Decoloración extrema.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Si la lona que antes era azul marino ahora es grisácea, o el granate parece rosa pálido, los rayos UV han degradado los pigmentos. Esto no es solo estético: significa que las fibras del tejido también han perdido resistencia.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-scissors-cut-line text-primary-600 text-sm"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Desgarros y agujeros.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Pequeños cortes en los bordes que antes no estaban, o agujeros en la zona central. Un desgarrón puede crecer rápidamente con el viento y destrozar la lona entera en un solo día de levante.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-drop-line text-primary-600 text-sm"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Manchas de humedad o moho.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    Manchas negras o verdosas que ya no desaparecen con la limpieza. En Málaga, la combinación de humedad nocturna y calor diurno crea el ambiente perfecto para que el moho se instale en lonas de poliéster de baja calidad.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-expand-horizontal-line text-primary-600 text-sm"></i>
                </div>
                <div>
                  <strong className="text-foreground-900">Pérdida de tensión.</strong>
                  <p className="text-sm text-foreground-600 mt-1">
                    La lona se ha estirado y ya no queda tirante al extender el toldo. Forma bolsas de agua cuando llueve y flamea con la más mínima brisa. Esto indica que las fibras han perdido su elasticidad original.
                  </p>
                </div>
              </div>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Tipos de tejido: ¿cuál elegir para Málaga?
            </h2>

            <p>
              No todos los tejidos aguantan igual el clima malagueño. Aquí va nuestra recomendación sincera, basada en años de experiencia:
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-background-200">
                    <th className="text-left py-3 px-4 font-semibold text-foreground-900">Tejido</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground-900">Duración en Málaga</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground-900">Precio orientativo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-200/70">
                    <td className="py-3 px-4 font-medium text-foreground-800">Acrílico 100% (Sunbrella, Recacril)</td>
                    <td className="py-3 px-4 text-foreground-600">8-12 años</td>
                    <td className="py-3 px-4 text-primary-600 font-semibold">desde 120 €</td>
                  </tr>
                  <tr className="border-b border-background-200/70">
                    <td className="py-3 px-4 font-medium text-foreground-800">Poliéster recubierto</td>
                    <td className="py-3 px-4 text-foreground-600">4-6 años</td>
                    <td className="py-3 px-4 text-primary-600 font-semibold">desde 80 €</td>
                  </tr>
                  <tr className="border-b border-background-200/70">
                    <td className="py-3 px-4 font-medium text-foreground-800">Microperforado (screen)</td>
                    <td className="py-3 px-4 text-foreground-600">5-7 años</td>
                    <td className="py-3 px-4 text-primary-600 font-semibold">desde 150 €</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm text-foreground-500 mt-2">
              * Precios orientativos para un toldo estándar de 3-4 metros, mano de obra incluida.
            </p>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              ¿Merece la pena cambiarla uno mismo?
            </h2>

            <p>
              Vamos al grano: cambiar una lona de toldo es de esas tareas que en YouTube parecen fáciles pero en la realidad son un quebradero de cabeza. Necesitas al menos dos personas, una escalera firme, y un día sin viento (algo no tan fácil de encontrar en Málaga según la temporada).
            </p>

            <p>
              El proceso implica: desatornillar la lona vieja del tubo de enrollado y del perfil delantero (a menudo con tornillos oxidados por el salitre), medir la nueva lona al milímetro, cortar si es necesario, fijarla al tubo perfectamente alineada, enrollarla con la tensión justa y fijar el perfil delantero. Un error de solo un centímetro en la alineación hace que la lona enrolle torcida y se dañe en semanas.
            </p>

            <p>
              Además, hay que considerar la seguridad: estás trabajando a cierta altura, con el toldo extendido, y cualquier movimiento en falso puede desequilibrarte. Si la lona es grande —como las de toldos de bares y restaurantes— el peso del tejido hace la maniobra aún más complicada.
            </p>

            <div className="my-8">
              <ConversionBlock
                title="¿Lona de toldo para cambiar en Málaga? Te la instalamos nosotros"
                subtitle="Trabajamos con tejidos acrílicos de primeras marcas. Medición, confección e instalación en 3-5 días. Presupuesto sin compromiso."
              />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 my-8">
              <PriceTag price="desde 120 €" description="Cambio lona acrílica" />
              <p className="text-sm text-foreground-500">
                Precio orientativo para lona acrílica estándar (3-4 m). Incluye tejido, confección e instalación. Lonas más grandes o microperforadas tienen un coste adicional.
              </p>
            </div>

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10">
              Preguntas frecuentes
            </h2>

            <GuiaFAQ faqs={FAQS} />

            <div className="mt-12">
              <GuiaRelacionadas />
            </div>

          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}